import { useCallback, useLayoutEffect, useState } from "react";

const EPSILON = 2;

/**
 * Tracks whether a horizontally scrollable element can scroll further left/right.
 * Use for overflow affordances (edge fade / chevron) on tab bars and top nav.
 *
 * @param {React.RefObject<HTMLElement|null>} ref
 * @returns {{ canScrollLeft: boolean, canScrollRight: boolean }}
 */
export function useHorizontalScrollOverflow(ref) {
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const update = useCallback(() => {
    const el = ref?.current;
    if (!el) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > EPSILON);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - EPSILON);
  }, [ref]);

  useLayoutEffect(() => {
    const el = ref?.current;
    if (!el) return undefined;

    update();
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);

    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(update);
      ro.observe(el);
    }

    let mo;
    if (typeof MutationObserver !== "undefined") {
      mo = new MutationObserver(update);
      mo.observe(el, { childList: true, subtree: true, characterData: true });
    }

    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      if (ro) ro.disconnect();
      if (mo) mo.disconnect();
    };
  }, [ref, update]);

  return { canScrollLeft, canScrollRight };
}
